import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/base/SEO';
import { getComicBySlug, allComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import ComicCard from '@/components/feature/ComicCard';

const CANONICAL_URL = 'https://www.hanmanzhijia.com';

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();

  const comic = useMemo(() => (slug ? getComicBySlug(slug) : undefined), [slug]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return allComics
      .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  const category = useMemo(() => {
    if (!comic) return undefined;
    return categories.find((c) => c.slug === comic.categorySlug);
  }, [comic]);

  const formatViews = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return String(n);
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `${CANONICAL_URL}/comic/${comic.slug}`,
    image: comic.cover,
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    inLanguage: 'zh-CN',
    genre: comic.category,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 5,
      ratingCount: Math.floor(comic.views / 100),
    },
  };

  return (
    <>
      <SEO
        title={`${comic.title} - ${comic.author}`}
        description={`${comic.title}：${comic.description.slice(0, 120)}。韩国漫画免费在线阅读，下拉式流畅体验，尽在韩漫之家。`}
        keywords={`韩国漫画免费,${comic.title},${comic.category},韩漫在线阅读`}
        canonicalUrl={`${CANONICAL_URL}/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        ogImageAlt={`${comic.title} - 韩国漫画封面`}
        publishedTime={comic.createdAt}
        modifiedTime={comic.updatedAt}
        schema={schema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: comic.category, to: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          {/* Hero Banner - Comic Detail */}
          <article className="flex flex-col lg:flex-row gap-8 mb-14">
            {/* Cover */}
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩国漫画`}
                  title={`${comic.title} - 韩国漫画免费在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}
                >
                  {comic.status}
                </span>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-2">
                {comic.title}
              </h1>

              <p className="text-foreground-500 text-sm mb-4">
                作者：<strong>{comic.author}</strong>
              </p>

              <div className="flex items-center gap-6 mb-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{comic.rating}</strong> / 5
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(comic.views)} 次阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-3-line"></i>
                  {comic.chapters.length} 话
                </span>
              </div>

              <p className="text-foreground-700 text-sm leading-relaxed mb-6 max-w-2xl">
                {comic.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                  开始阅读 第1话
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-all duration-200 cursor-pointer">
                  <i className="ri-heart-line"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-all duration-200 cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                </button>
              </div>
            </div>
          </article>

          {/* Chapters */}
          <section className="mb-14">
            <h2 className="font-heading text-xl text-foreground-900 mb-4">章节列表</h2>
            <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200/70">
                {comic.chapters.map((ch) => (
                  <div
                    key={ch.id}
                    className="flex items-center justify-between p-3 bg-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-400 w-12 text-right">{ch.number}.</span>
                      <span className="text-sm text-foreground-800">
                        {ch.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400">{ch.createdAt}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="pb-14">
              <h2 className="font-heading text-xl text-foreground-900 mb-6">同类推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="normal" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}